import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCurrentUserRoles } from 'hooks/useCurrentUserRoles';
import { StreetOutreachWorkerView } from '../components/StreetOutreachWorkerView';
import { AnalystView } from '../components/AnalystView';
import { PageTitle, PeriodFilterControls } from 'components';
import { Dashboard } from '@mui/icons-material';
import { TeamDropdownFilter, TimeRange } from '../components/DropdownFilter';
import { useIntl } from 'react-intl';
import { useTheme } from '@mui/material';
import { getTenantIdentifier } from 'utils/tenantUtils';
import dayjs from 'dayjs';

type FilterStorageType = {
  timeFilter: TimeRange,
  teamFilter: string
  customDateRange?: CustomDateRange
}

type CustomDateRange = {
  startDate: string
  endDate: string
}

export const HomePage: React.FC = () => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const [timeRangeFilter, setTimeRangeFilter] = useState(TimeRange.last7Days);
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const defaultCustomDateRange = useMemo(() => ({
    startDate: dayjs().subtract(7, 'day').startOf('day').utc().toISOString(),
    endDate: dayjs().endOf('day').utc().toISOString()
  }), []);
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>(defaultCustomDateRange);
  const { isAdministrator, isAnalyst } = useCurrentUserRoles();
  const dashboardFiltersKey = `${getTenantIdentifier()}-dashboard-${isAdministrator || isAnalyst ? 'admin' : 'streetWorker'}`;

  const getStoredFilters = useCallback(() => {
    const stateJSON = localStorage.getItem(dashboardFiltersKey);
    return stateJSON ? JSON.parse(stateJSON ?? '{}') as FilterStorageType : null;
  }, [dashboardFiltersKey]);

  const getDefaultFilters = useCallback((): FilterStorageType => ({
    timeFilter: TimeRange.last7Days,
    teamFilter: 'all',
    customDateRange: defaultCustomDateRange
  }), [defaultCustomDateRange]);

  const setStoredFilters = useCallback((filters: FilterStorageType) => {
    localStorage.setItem(dashboardFiltersKey, JSON.stringify(filters));
  }, [dashboardFiltersKey]);

  const updateStoredFilters = useCallback((updates: Partial<FilterStorageType>) => {
    const current = getStoredFilters() ?? getDefaultFilters();
    setStoredFilters({ ...current, ...updates });
  }, [getDefaultFilters, getStoredFilters, setStoredFilters]);

  const handleChangeTimeFilter = (timeFilter: TimeRange) => {
    setTimeRangeFilter(timeFilter);
    if (timeFilter === TimeRange.customRange && !customDateRange.startDate && !customDateRange.endDate) {
      setCustomDateRange(defaultCustomDateRange);
      updateStoredFilters({ timeFilter, customDateRange: defaultCustomDateRange });
      return;
    }
    updateStoredFilters({ timeFilter });
  };

  const handleChangeTeamFilter = (teamFilter: string) => {
    setTeamFilter(teamFilter);
    updateStoredFilters({ teamFilter });
  };

  useEffect(() => {
    const storedFilters = getStoredFilters();
    if (storedFilters) {
      setTimeRangeFilter(storedFilters.timeFilter);
      setTeamFilter(storedFilters.teamFilter);
      setCustomDateRange(storedFilters.customDateRange ?? defaultCustomDateRange);
    } else {
      setStoredFilters(getDefaultFilters());
    }
  }, [defaultCustomDateRange, getDefaultFilters, getStoredFilters, setStoredFilters]);

  const handleChangeCustomDateRange = (updates: Partial<CustomDateRange>) => {
    setCustomDateRange(prev => {
      const next = { ...prev, ...updates };
      updateStoredFilters({ customDateRange: next });
      return next;
    });
  };

  return (
    <>
      <PageTitle icon={<Dashboard />} title={formatMessage({ id: 'Home' })}
        actionButton={
          <PeriodFilterControls
            timeRange={timeRangeFilter}
            onTimeRangeChange={handleChangeTimeFilter}
            customDateRange={customDateRange}
            onCustomDateRangeChange={handleChangeCustomDateRange}
            leadingFilters={
              (isAdministrator || isAnalyst) ? (
                <TeamDropdownFilter
                  value={teamFilter}
                  onChangeValue={handleChangeTeamFilter}
                  size='small'
                  fullWidth
                  sx={{
                    width: { md: theme.spacing(31) },
                    flex: { xs: 1, md: 'none' }
                  }}
                />
              ) : null
            }
          />
        }
      />
      {isAdministrator || isAnalyst
        ?
        <AnalystView
          teamFilter={teamFilter}
          timeRangeFilter={timeRangeFilter}
          customDateRange={customDateRange}
        />
        :
        <StreetOutreachWorkerView
          timeRangeFilter={timeRangeFilter}
          customDateRange={customDateRange}
        />
      }
    </>
  );
};